import { useEffect, useMemo, useRef, useState } from "react";
import {
  Coins,
  Calculator,
  Camera,
  Sparkle,
  CheckCircle,
  CircleNotch,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

// Purity factor = pure-metal fraction. Used to derive melt rate per gram from the
// fully-pure (24K / 999 silver / 950 platinum) base rate.
const PURITY_FACTOR = {
  // Gold
  "24K": 1.0,
  "22K": 0.916,
  "20K": 0.833,
  "18K": 0.75,
  "14K": 0.585,
  "9K": 0.375,
  "Other (Gold)": 0.5, // user-quoted estimate; conservative default
  // Silver (millesimal fineness / 1000)
  "999 Silver": 0.999,
  "925 Silver": 0.925,
  "800 Silver": 0.800,
  "750 Silver": 0.750,
  "650 Silver": 0.650,
  "500 Silver": 0.500,
  "Other (Silver)": 0.500,
  // Platinum
  "950 Platinum": 0.950,
  "Other (Platinum)": 0.85,
};

const CONDITION_PREMIUM = {
  Excellent: 0.08,
  Good: 0.05,
  Fair: 0.03,
  Damaged: 0,
};

export default function Resell() {
  const [metal, setMetal] = useState("Gold");
  const [purity, setPurity] = useState("22K");
  const [weight, setWeight] = useState(10);
  const [condition, setCondition] = useState("Good");
  const [hasStones, setHasStones] = useState(false);
  const [stoneValue, setStoneValue] = useState(0);
  const [rates, setRates] = useState({ "22k": 6600, silver: 95 });
  const [calculated, setCalculated] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [assessing, setAssessing] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/gold-rates?city=chennai").then((r) => setRates(r.data.rates)).catch(() => {});
  }, []);

  const purityOptions = useMemo(() => {
    if (metal === "Silver") {
      return ["999 Silver", "925 Silver", "800 Silver", "750 Silver", "650 Silver", "500 Silver", "Other (Silver)"];
    }
    if (metal === "Platinum") return ["950 Platinum", "Other (Platinum)"];
    return ["24K", "22K", "20K", "18K", "14K", "9K", "Other (Gold)"];
  }, [metal]);

  useEffect(() => {
    if (!purityOptions.includes(purity)) {
      setPurity(purityOptions[0]);
    }
  }, [purityOptions, purity]);

  const ratePerGram = useMemo(() => {
    if (metal === "Silver") {
      // rates.silver is the 999-silver per-gram rate (treated as base). Scale by purity.
      const base999 = rates.silver || 95;
      const factor = PURITY_FACTOR[purity] ?? 0.925;
      return Math.round(base999 * (factor / 0.999));
    }
    if (metal === "Platinum") {
      const platBase950 = 3000; // approximate INR/g for 950 platinum
      const factor = PURITY_FACTOR[purity] ?? 0.95;
      return Math.round(platBase950 * (factor / 0.95));
    }
    // Gold — derive from 22K board rate
    const base22 = rates["22k"] || 6600;
    const factor = PURITY_FACTOR[purity] ?? 0.916;
    return Math.round(base22 * (factor / 0.916));
  }, [metal, purity, rates]);

  const purityFactor = PURITY_FACTOR[purity] ?? 0.916;

  const calc = useMemo(() => {
    const w = Number(weight) || 0;
    const stones = hasStones ? Number(stoneValue) || 0 : 0;
    // ratePerGram is already the per-gram rate AT this purity; melt value = weight × rate.
    const meltValue = w * ratePerGram;
    const conditionPremium = CONDITION_PREMIUM[condition] ?? 0.05;
    const fairResale = meltValue * (1 + conditionPremium) + stones * 0.7;
    const shopOffer = meltValue * 0.95 + stones * 0.5;
    return { meltValue, fairResale, shopOffer, stones };
  }, [weight, ratePerGram, condition, hasStones, stoneValue]);

  const calculate = () => {
    if (!Number(weight)) {
      toast.error("Enter weight");
      return;
    }
    setCalculated(true);
  };

  const onPhoto = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Choose an image");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setAssessment(null);
  };

  const assessPhoto = async () => {
    if (!photoFile) return;
    setAssessing(true);
    try {
      const fd = new FormData();
      fd.append("file", photoFile);
      const r = await api.post("/resell/assess", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAssessment(r.data);
      if (r.data.condition && CONDITION_PREMIUM[r.data.condition] !== undefined) {
        setCondition(r.data.condition);
        toast.success(`Condition updated to ${r.data.condition} from photo`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not assess photo");
    } finally {
      setAssessing(false);
    }
  };

  return (
    <div data-testid="resell-page">
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
          Resale Valuator
        </p>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#0A1128] tracking-tight">
          Know your gold's <span className="gold-text">true value</span> before you sell.
        </h1>
        <p className="mt-3 text-[#4A5568] max-w-2xl">
          Mine AI calculates the fair resale price so shops can't underpay you.
        </p>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="mc-card p-6 sm:p-8" data-testid="resell-form">
            <h2 className="font-display text-2xl text-[#0A1128]">Your jewellery</h2>

            <div className="mt-5 space-y-5">
              {/* Metal */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">Metal</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {["Gold", "Silver", "Platinum"].map((m) => (
                    <button
                      key={m}
                      data-testid={`metal-${m}`}
                      onClick={() => setMetal(m)}
                      className={`py-2.5 rounded-md border smooth ${
                        metal === m
                          ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB] font-medium"
                          : "border-[#E8DFD1] hover:border-[#3B5BDB]/50"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Purity */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">Purity</label>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {purityOptions.map((p) => (
                    <button
                      key={p}
                      data-testid={`purity-${p}`}
                      onClick={() => setPurity(p)}
                      className={`py-2.5 rounded-md border smooth text-sm ${
                        purity === p
                          ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB] font-medium"
                          : "border-[#E8DFD1] hover:border-[#3B5BDB]/50"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Weight */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                  Weight (grams)
                </label>
                <input
                  data-testid="resell-weight"
                  type="number"
                  step="0.1"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  placeholder="e.g. 10"
                />
                <p className="text-[11px] text-[#4A5568] mt-1">
                  Tip: ask any jeweller to weigh it — usually free.
                </p>
              </div>

              {/* Condition */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">Condition</label>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {Object.keys(CONDITION_PREMIUM).map((c) => (
                    <button
                      key={c}
                      data-testid={`condition-${c}`}
                      onClick={() => setCondition(c)}
                      className={`py-2.5 rounded-md border smooth text-sm ${
                        condition === c
                          ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB] font-medium"
                          : "border-[#E8DFD1] hover:border-[#3B5BDB]/50"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stones */}
              <div>
                <label className="text-xs uppercase tracking-wider text-[#4A5568]">
                  Has stones / diamonds?
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    data-testid="stones-yes"
                    onClick={() => setHasStones(true)}
                    className={`px-4 py-2 rounded-md border smooth text-sm ${
                      hasStones ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB]" : "border-[#E8DFD1]"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    data-testid="stones-no"
                    onClick={() => {
                      setHasStones(false);
                      setStoneValue(0);
                    }}
                    className={`px-4 py-2 rounded-md border smooth text-sm ${
                      !hasStones ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB]" : "border-[#E8DFD1]"
                    }`}
                  >
                    No
                  </button>
                </div>
                {hasStones && (
                  <input
                    data-testid="stone-value"
                    type="number"
                    value={stoneValue}
                    onChange={(e) => setStoneValue(e.target.value)}
                    placeholder="Approx stone value (₹)"
                    className="mt-2 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  />
                )}
              </div>

              <button
                data-testid="calculate-btn"
                onClick={calculate}
                className="w-full h-11 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth flex items-center justify-center gap-2"
              >
                <Calculator size={18} weight="duotone" />
                Calculate value
              </button>
            </div>
          </div>

          {/* Result */}
          <div className="space-y-6">
            <div
              className={`mc-card p-6 sm:p-8 ${calculated ? "" : "opacity-60"}`}
              data-testid="resell-result"
            >
              <h2 className="font-display text-2xl text-[#0A1128]">Three numbers to know</h2>
              <p className="text-sm text-[#4A5568] mt-1">
                {calculated ? "Use these to negotiate." : "Fill the form to see numbers."}
              </p>

              <div className="mt-5 space-y-3">
                <ValueBox
                  tone="grey"
                  label="Melt value"
                  sub="Minimum you should accept — pure metal value"
                  value={calc.meltValue}
                  testid="melt-value"
                />
                <ValueBox
                  tone="green"
                  label="Fair resale"
                  sub="What you deserve — based on condition"
                  value={calc.fairResale}
                  testid="fair-resale"
                />
                <ValueBox
                  tone="amber"
                  label="What shops typically offer"
                  sub="Most shops offer this — always negotiate"
                  value={calc.shopOffer}
                  testid="shop-offer"
                />
              </div>
            </div>

            <div className="mc-card p-6" data-testid="ai-tips">
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="duotone" className="text-[#D4AF37]" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold">
                  Mine AI advice
                </p>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-[#0A1128]/90">
                <li className="flex gap-2">
                  <CheckCircle size={16} weight="duotone" className="text-[#16A34A] shrink-0 mt-0.5" />
                  Get weighed at 2-3 shops — compare offered rates.
                </li>
                <li className="flex gap-2">
                  <CheckCircle size={16} weight="duotone" className="text-[#16A34A] shrink-0 mt-0.5" />
                  Ask which gold rate they're using — should match the city board rate.
                </li>
                <li className="flex gap-2">
                  <CheckCircle size={16} weight="duotone" className="text-[#16A34A] shrink-0 mt-0.5" />
                  Brand exchange schemes (Tanishq, Malabar) often give better value.
                </li>
                <li className="flex gap-2">
                  <CheckCircle size={16} weight="duotone" className="text-[#16A34A] shrink-0 mt-0.5" />
                  Never sell on the spot — take 24 hours, then decide.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Photo assessment */}
        <div className="mt-10 mc-card p-6 sm:p-8" data-testid="photo-assess">
          <div className="flex items-center gap-2">
            <Camera size={20} weight="duotone" className="text-[#3B5BDB]" />
            <h3 className="font-display text-2xl text-[#0A1128]">
              Optional: photo for accurate condition
            </h3>
          </div>
          <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">
            Upload a clear photo — Mine AI Vision will assess condition and design premium for a sharper estimate.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="resell-photo-input"
            onChange={(e) => onPhoto(e.target.files?.[0])}
          />
          <div className="mt-5 grid lg:grid-cols-2 gap-5">
            <div>
              {photoPreview ? (
                <div className="space-y-3">
                  <img
                    src={photoPreview}
                    alt="preview"
                    className="rounded-lg w-full max-h-72 object-contain border border-[#E8DFD1] bg-[#F5EFE6]/40"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="flex-1 py-2.5 rounded-md border border-[#E8DFD1] text-sm hover:border-[#3B5BDB] smooth"
                    >
                      Change
                    </button>
                    <button
                      data-testid="assess-photo-btn"
                      onClick={assessPhoto}
                      disabled={assessing}
                      className="flex-1 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {assessing ? (
                        <>
                          <CircleNotch size={14} className="animate-spin" weight="bold" /> Assessing...
                        </>
                      ) : (
                        "Assess condition"
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="upload-photo-btn"
                  className="w-full h-44 rounded-lg border-2 border-dashed border-[#E8DFD1] hover:border-[#3B5BDB]/60 smooth flex flex-col items-center justify-center text-[#4A5568]"
                >
                  <Camera size={32} weight="duotone" className="text-[#3B5BDB]" />
                  <span className="mt-2 text-sm">Upload jewellery photo</span>
                </button>
              )}
            </div>
            <div>
              {assessment ? (
                <pre
                  data-testid="assessment-text"
                  className="whitespace-pre-wrap text-sm text-[#0A1128]/90 font-body bg-[#F5EFE6]/40 p-4 rounded-lg border border-[#E8DFD1] h-72 overflow-y-auto"
                >
                  {assessment.assessment}
                </pre>
              ) : (
                <div className="h-44 lg:h-full flex items-center justify-center text-sm text-[#4A5568] border border-dashed border-[#E8DFD1] rounded-lg p-4">
                  Upload a photo and Mine AI will tell you the real condition.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ValueBox({ tone, label, sub, value, testid }) {
  const tones = {
    grey: "bg-[#F5EFE6] text-[#0A1128] border-[#E8DFD1]",
    green: "bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/20",
    amber: "bg-[#EAB308]/10 text-[#B45309] border-[#EAB308]/30",
  };
  return (
    <div
      className={`rounded-lg border p-4 ${tones[tone]}`}
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between">
        <p className="text-xs uppercase tracking-wider font-semibold">{label}</p>
        <p className="font-display text-2xl tabular-nums font-semibold">
          ₹{Math.round(value).toLocaleString("en-IN")}
        </p>
      </div>
      <p className="text-xs mt-1 opacity-80">{sub}</p>
    </div>
  );
}
